/**
 * Content-QA route for lessons (mirrors SkillTreeReview and the exam's
 * /test-review). Lists a unit's lessons in play order and plays any of them
 * with no sign-in, no paywall and nothing saved (LessonDemoPlayer) — so
 * Tornike can check a rebuilt unit on a preview link: Google sign-in only
 * returns to app.fluent.ge, and most lessons are Pro.
 *
 * Preview hosts only. On app.fluent.ge (and on the project's own
 * fluent-ge-app.pages.dev name, which serves production) it redirects home,
 * so it never becomes a way around Pro.
 *
 *   /lesson-review?unit=unit-01-basics   the unit's lessons ("Lesson N")
 *   /lesson-review?course=phrases        the phrases course, level by level
 *   /lesson-review/:lessonId             play one
 *
 * Phrases-course lessons are lazy (content/lessons.ts): both phrases pages
 * fetch them first and show a skeleton meanwhile.
 */
import { Navigate, useLocation, useNavigate, useParams } from 'react-router';
import { Button, Container, Heading, Muted, NavRow, RowGroup, Screen, Skeleton, Stack } from '@fluent/ui';
import { courseOfLesson, getCourseTree, isPhrasesLessonId, PHRASES_COURSE_ID } from './content/course.js';
import { getLesson } from './content/lessons.js';
import { usePhrasesContent, type PhrasesContent } from './content/usePhrasesContent.js';
import { LessonDemoPlayer } from './lesson/LessonPlayer.js';
import { isReviewHost } from './lib/reviewHost.js';

function unitOfLesson(lessonId: string): string | undefined {
  return getCourseTree()?.units.find((u) =>
    u.resolvedSkills.some((s) => s.resolvedLessons.some((l) => l.id === lessonId)),
  )?.id;
}

/** Waiting for the lazy phrases lessons: a skeleton, or a retry when the
 *  download failed (preview-only page, so plain English). */
function PhrasesWait({ content }: { content: PhrasesContent }) {
  return (
    <Screen>
      <Container size="sm">
        <Stack gap={4} style={{ padding: 'var(--space-6, 24px) 0' }}>
          {content.status === 'error' ? (
            <>
              <Muted>The phrases lessons did not load.</Muted>
              <Button variant="secondary" onClick={content.retry}>
                Try again
              </Button>
            </>
          ) : (
            <Skeleton shape="card" style={{ height: 280 }} data-testid="phrases-review-loading" />
          )}
        </Stack>
      </Container>
    </Screen>
  );
}

export function LessonReview() {
  const navigate = useNavigate();
  if (!isReviewHost(window.location.hostname)) return <Navigate to="/" replace />;
  const search = new URLSearchParams(window.location.search);
  if (search.get('course') === 'phrases') return <PhrasesLessonList />;
  const tree = getCourseTree();
  if (!tree) return <Muted>No course tree.</Muted>;
  const wanted = search.get('unit');
  const unit = tree.units.find((u) => u.id === wanted) ?? tree.units[0];
  if (!unit) return <Muted>No units.</Muted>;
  let position = 0;
  return (
    <Screen>
      <Container size="sm">
        <Stack gap={6} style={{ padding: 'var(--space-6, 24px) 0' }}>
          <Stack gap={1}>
            <Heading level={2}>{unit.title}</Heading>
            <Muted>
              {unit.title_en} · preview — nothing is saved. New sentences have no sound yet.
            </Muted>
          </Stack>
          {unit.resolvedSkills.map((skill) => (
            <Stack gap={2} key={skill.id}>
              <Heading level={4}>{skill.title}</Heading>
              <RowGroup>
                {skill.resolvedLessons.map((l, i) => {
                  position += 1;
                  const n = position;
                  const count = getLesson(l.id)?.exercises.length ?? 0;
                  return (
                    <NavRow
                      key={l.id}
                      variant="row"
                      title={`${n}. ${l.title}`}
                      sub={`${i === skill.resolvedLessons.length - 1 ? 'გამეორება · ' : ''}${count} სავარჯიშო`}
                      onPress={() => navigate(`/lesson-review/${l.id}`)}
                      data-testid={`review-lesson-${l.id}`}
                    />
                  );
                })}
              </RowGroup>
            </Stack>
          ))}
        </Stack>
      </Container>
    </Screen>
  );
}

/** The phrases course (ka-en-phrases): every level's lessons in play order,
 *  numbered "lesson N of the level", reviews marked. */
function PhrasesLessonList() {
  const navigate = useNavigate();
  const content = usePhrasesContent();
  if (content.status !== 'ready') return <PhrasesWait content={content} />;
  const tree = getCourseTree(PHRASES_COURSE_ID);
  if (!tree) return <Muted>The phrases course is not in this build yet.</Muted>;
  return (
    <Screen>
      <Container size="sm">
        <Stack gap={6} style={{ padding: 'var(--space-6, 24px) 0' }}>
          <Stack gap={1}>
            <Heading level={2}>{tree.course.title}</Heading>
            <Muted>phrases course · preview — nothing is saved.</Muted>
          </Stack>
          {tree.units.map((unit) => (
            <Stack gap={2} key={unit.id}>
              <Heading level={4}>
                {unit.title} · {unit.title_en}
              </Heading>
              <RowGroup>
                {unit.resolvedSkills
                  .flatMap((s) => s.resolvedLessons)
                  .map((l, i) => {
                    const review = !l.exercises.some((e) => e.type === 'phrase_intro');
                    return (
                      <NavRow
                        key={l.id}
                        variant="row"
                        title={`${i + 1}. ${l.tile_en ? `${l.tile_en} — ` : ''}${l.title}`}
                        sub={`${review ? 'გამეორება · ' : ''}${l.exercises.length} სავარჯიშო · ${l.id}`}
                        onPress={() =>
                          navigate(`/lesson-review/${l.id}`, { state: { from: '/lesson-review?course=phrases' } })
                        }
                        data-testid={`review-lesson-${l.id}`}
                      />
                    );
                  })}
              </RowGroup>
            </Stack>
          ))}
        </Stack>
      </Container>
    </Screen>
  );
}

/** Where ✕ / finish return to: the page the lesson was opened from when it
 *  is a review page (the phrases design route or a list), else the lesson's
 *  own list. */
function reviewExitTo(lessonId: string, from: unknown): string {
  if (typeof from === 'string' && /^\/(lesson-review|phrases-review)(\?|$)/u.test(from)) return from;
  if (courseOfLesson(lessonId) === PHRASES_COURSE_ID) return '/lesson-review?course=phrases';
  const unitId = unitOfLesson(lessonId);
  return `/lesson-review${unitId ? `?unit=${unitId}` : ''}`;
}

export function LessonReviewPlayer() {
  const { lessonId } = useParams<{ lessonId: string }>();
  const location = useLocation();
  const reviewHost = isReviewHost(window.location.hostname);
  // A phrases lesson waits for the lazy phrases lessons; main ones never do.
  const isPhrases = lessonId ? isPhrasesLessonId(lessonId) : false;
  const content = usePhrasesContent(reviewHost && isPhrases);
  if (!reviewHost) return <Navigate to="/" replace />;
  const lesson = lessonId ? getLesson(lessonId) : null;
  if (!lesson && isPhrases && content.status !== 'ready') return <PhrasesWait content={content} />;
  if (!lesson) return <Navigate to="/lesson-review" replace />;
  const from: unknown = (location.state as { from?: unknown } | null)?.from;
  return <LessonDemoPlayer key={lesson.id} lesson={lesson} exitTo={reviewExitTo(lesson.id, from)} />;
}
